/**
 * Exporting ApolloClientGenerator and KeyValueStoreGeneratorClass wrapped in a "class" for the VizClient Saltarelle code,
 * which doesn't allow functions to exist outside of classes.
 */
import * as ApolloClientGeneratorClass from '@tableau/data-catalog-shared';
import * as GraphQLGlobalTypes from '../gen-src/queries/globalTypes';
import * as KeyValueStoreGeneratorClass from './components/DataQualityWarnings/keyValueStoreGenerator';

export { ApolloClientGeneratorClass, KeyValueStoreGeneratorClass };
export { GraphQLGlobalTypes };

/**
 * This is your main. This is where you re-export everything you want to be publicly available.
 *
 * The build enforces that the file has the same name as the global variable that is exported.
 */


/*
 * --------------------- *
 * UTILITIES AND HELPERS *
 * --------------------- *
 */

export {
  ApiRequests,
  ApiRequestsTypes
} from '@tableau/data-catalog-shared';

export {
  UIActions
} from '@tableau/data-catalog-shared';

export {
  FeatureFlags, FeatureFlagsProvider
} from '@tableau/data-catalog-shared';

export {
  apolloClientGenerator,
  DataCatalogApolloClient,
} from '@tableau/data-catalog-shared';

export {
  FormattedMarkupWrapperInPage,
  FormattedMarkupWrapperInTooltip,
  FormattedMarkupWrapperProps,
  MarkupPageStyleClass,
  MarkupTooltipStyleClass,
} from '@tableau/data-catalog-shared';


export { GraphQLError } from '@tableau/data-catalog-shared';

export {
  ConnectionListResult,
  GraphQLSliceable,
  GraphQLSliceableParams,
  PaginationQueryVariables,
  PartialQueryVariables,
  SliceResult,
} from '@tableau/data-catalog-shared';

export { TableauUser } from '@tableau/data-catalog-shared';
export { OrderBy } from '@tableau/data-catalog-shared';
export { createServerOrPathString } from '@tableau/data-catalog-shared';

export { cellClickingTelemetryContext } from './components/datagrid/cellFormatters/LinkCellFormatter';

/*
 * ------------------------------------------------------------------------------------ *
 * LIST VIEW FRAGMENTS AND GENERATED TYPES FOR GRIDS IMPLEMENTED AS VIZPORTAL EXPLORERS *
 * ------------------------------------------------------------------------------------ *
 */

export {
  IOrderClause,
} from '@tableau/data-catalog-shared';

export { StandaloneErrorToasts as CatalogStandaloneErrorToasts } from './components/error/components/StandaloneErrorToasts';

export {
  columnsForVizPortal as TablesDisplayGrid_columns,
  tablesWithCrassCellFormatters
} from './components/sharedDisplayGrid/TablesDisplayGrid';

export {
  TablesDefaultDirections,
  TablesDefaultSortField,
  TablesValidSortFields
} from './components/sorting/sortProviders/TablesGridSortProvider';

export {
  QueryResultListNode as TablesExplorerQueryGrid_QueryResultListNode,
  QueryResults as TablesExplorerQueryGrid_QueryResults,
  QueryVariables as TablesExplorerQueryGrid_QueryVariables,
  selectConnectionListResult as TablesExplorerQueryGrid_selectConnectionListResult,
  QueryResultListNodeWithTagFilter as TablesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
  QueryResultsWithTagFilter as TablesExplorerQueryGrid_QueryResultsWithTagFilter,
  QueryVariablesWithTagFilter as TablesExplorerQueryGrid_QueryVariablesWithTagFilter,
  selectConnectionListResultWithTagFilter as TablesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
} from './components/CatalogBrowser/TablesExplorerQueryGrid';

export {
  columnsForVizPortal as DatabasesDisplayGrid_columns,
  databasesWithCrassCellFormatters
} from './components/sharedDisplayGrid/DatabasesDisplayGrid';

export {
  DatabasesDefaultDirections,
  DatabasesDefaultSortField,
  DatabasesValidSortFields
} from './components/sorting/sortProviders/DatabasesGridSortProvider';

export {
  QueryResultListNode as DatabasesExplorerQueryGrid_QueryResultListNode,
  QueryResults as DatabasesExplorerQueryGrid_QueryResults,
  QueryVariables as DatabasesExplorerQueryGrid_QueryVariables,
  selectConnectionListResult as DatabasesExplorerQueryGrid_selectConnectionListResult,
  QueryResultListNodeWithTagFilter as DatabasesExplorerQueryGrid_QueryResultListNodeWithTagFilter,
  QueryResultsWithTagFilter as DatabasesExplorerQueryGrid_QueryResultsWithTagFilter,
  QueryVariablesWithTagFilter as DatabasesExplorerQueryGrid_QueryVariablesWithTagFilter,
  selectConnectionListResultWithTagFilter as DatabasesExplorerQueryGrid_selectConnectionListResultWithTagFilter,
} from './components/CatalogBrowser/DatabasesExplorerQueryGrid';

export {
  getRowDataWithMaxCountsForDatabase as DatabaseRowDataFunctions_getRowDataWithMaxCountsForDatabase,
  getRowDataWithMaxCountsForDatabaseWithTagFilter as DatabaseRowDataFunctions_getRowDataWithMaxCountsForDatabaseWithTagFilter,
} from './components/database/functions/DatabaseRowDataFunctions';

export {
  DatabasesExternalAssetsDisplayGridRowData,
} from './components/database/types/DatabasesDisplayGridRowData';

export {
  getRowDataWithMaxCountsForTable as TableRowDataFunctions_getRowDataWithMaxCountsForTable,
  getRowDataWithMaxCountsForTableWithTagFilter as TableRowDataFunctions_getRowDataWithMaxCountsForTableWithTagFilter,
} from './components/table/functions/TableRowDataFunctions';

export {
  TablesExternalAssetsDisplayGridRowData,
} from './components/table/types/TablesDisplayGridRowData';

export {
  buildDefaultOrder,
  DefaultDirections,
} from '@tableau/data-catalog-shared';

/*
 * --------------- *
 * GRAPHQL QUERIES *
 * --------------- *
 */

export { DatabasesQueryGql } from './queries/DatabasesQueryGql';
export { DatabasesByTagQueryGql } from './queries/DatabasesByTagQueryGql';
export { DatabasesQueryByIdsGql } from './queries/DatabasesQueryByIdsGql';
export {
  DatabasesQueryByIds,
  DatabasesQueryByIdsVariables,
  DatabasesQueryByIds_databasesConnection_nodes as DatabasesQueryByIdsNodes
} from '../gen-src/queries/DatabasesQueryByIds';

export { DatabaseServerOrPathFragment } from '../gen-src/queries/DatabaseServerOrPathFragment';

export { TablesQueryGql } from './queries/TablesQueryGql';
export { TablesByTagQueryGql } from './queries/TablesByTagQueryGql';
export {
  TablesQuery_databaseTablesConnection_nodes
} from '../gen-src/queries/TablesQuery';

export { TablesDisplayPlacePageData } from './components/table/types/TablesDisplayPlacePageData';
export { DatabasesDisplayPlacePageData } from './components/database/types/DatabasesDisplayPlacePageData';

export {
  TagsQueryWithFiltersGql,
  TagsQueryWithFilters,
  TagsQueryWithFilters_tagsConnection_nodes,
  TagsQueryWithFiltersVariables,
} from '@tableau/data-catalog-shared';

/*
 * ----------------------- *
 * CERTIFICATION *
 * ----------------------- *
 */

export { CertificationWidget, CertificationWidgetProps } from '@tableau/data-catalog-shared';

/*
 * ----------------------- *
 * DATA QUALITY INDICATORS *
 * ----------------------- *
 */

export {
  AssetMarkedByIndicator,
  WarningStatusChip,
  WarningStatusIcon,
  WarningStatusProps,
  CertificationStatusChipOrIcon,
  CertificationStatusProps,
  EmbeddableCertificationStatusChipOrIcon,
  EmbeddableWarningStatusChip,
  EmbeddableWarningStatusIcon,
} from '@tableau/data-catalog-shared';

export {
  GraphDataQualityWarning
} from '@tableau/data-catalog-shared';

export {
  DataQualityType,
  localizeDataQualityTypeName
} from '@tableau/data-catalog-shared';

export {
  DataQualityAssetType,
  DataQualityDetailProps
} from '@tableau/data-catalog-shared';

/*
 * -------------------------------------------- *
 * EMBEDDABLE DATA QUALITY INDICATOR COMPONENTS *
 * -------------------------------------------- *
 */

export {
  EmbeddableDataQualityStatusChip
} from '@tableau/data-catalog-shared';

export {
  EmbeddableWorkbookDataQualityWarning,
  EmbeddableWorkbookDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningWorkbooks';

export {
  EmbeddablePublishedDatasourceDataQualityWarning,
  EmbeddablePublishedDatasourceDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningPublishedDatasources';

export {
  EmbeddedDatasourceDataQualityWarning,
  EmbeddedDatasourceDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningEmbeddedDatasources';

export {
  EmbeddableFlowDataQualityWarning,
  EmbeddableFlowDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningFlows';

export {
  EmbeddableMetricDataQualityWarning,
  EmbeddableMetricDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningMetrics';

export {
  EmbeddableVirtualConnectionDataQualityWarning,
  EmbeddableVirtualConnectionDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningVirtualConnections';

export {
  EmbeddableVirtualConnectionTableDataQualityWarning,
  EmbeddableVirtualConnectionTableDataQualityWarningProps
} from './components/DataQualityWarnings/DataQualityWarningVirtualConnectionTables';

/*
 * ----------------------------------- *
 * EMBEDDABLE COMPONENTS FOR VIZPORTAL *
 * ----------------------------------- *
 */

export {
  VizPortalSearchResultsDataCatalogProvider,
  VizPortalDQWDataCatalogProvider
} from '@tableau/data-catalog-shared';

export {
  EmbeddableDatasourceSubplaceWithoutRouter,
  EmbeddableDatasourceSubplaceProps,
} from './components/DatasourcePlacePage/EmbeddableDatasourceSubplace';

export {
  EmbeddableCatalogBrowserWithoutRouter,
  EmbeddableCatalogBrowserProps,
} from './components/CatalogBrowser/EmbeddableCatalogBrowser';

export {
  EmbeddableWorkbookSubplaceWithoutRouter,
  EmbeddableWorkbookSubplaceProps,
} from './components/WorkbookPlacePage/EmbeddableWorkbookSubplace';

export {
  EmbeddableFlowSubplaceWithoutRouter,
  EmbeddableFlowSubplaceProps,
} from './components/FlowPlacePage/EmbeddableFlowSubplace';

export {
  EmbeddableLensSubplaceWithoutRouter,
  EmbeddableLensSubplaceProps,
} from './components/LensPlacePage/EmbeddableLensSubplace';

export {
  EmbeddableVirtualConnectionSubplaceWithoutRouter,
  EmbeddableVirtualConnectionSubplaceProps,
} from './components/VirtualConnectionPlacePage/EmbeddableVirtualConnectionSubplace';

export {
  EmbeddableVirtualConnectionTableSubplaceWithoutRouter,
  EmbeddableVirtualConnectionTableSubplaceProps,
} from './components/VirtualConnectionTablePlacePage/EmbeddableVirtualConnectionTableSubplace';

/*
 * ----------------------------------- *
 * EMBEDDABLE COMPONENTS FOR VIZCLIENT *
 * ----------------------------------- *
 */

export {
  EmbeddableVizClientLineagePanel,
  EmbeddableVizClientLineagePanelProps,
} from './components/vizclient/EmbeddableVizClientLineagePanel';

export {
  DataQualityWarningsInWebAuthoring,
  DataQualityWarningsInWebAuthoringProps,
} from './components/DataQualityWarnings/DataQualityWarningsInWebAuthoring';
